import { APP_VERSION } from "./version";

export type ClientSurface = "ios_app" | "android_app" | "web";

export interface ClientMetadata {
  surface: ClientSurface;
  appVersion: string;
  webVersion: string;
  buildNumber?: string;
}

export function withClientMetadata<T extends Record<string, unknown>>(
  payload: T,
  client: ClientMetadata,
): T & { client: ClientMetadata } {
  return { ...payload, client };
}

export const MAX_CLIENT_RELEASE_VALUE_LENGTH = 32;

interface CapacitorAppInfo {
  version?: unknown;
  build?: unknown;
}

interface CapacitorAppPlugin {
  getInfo?: () => Promise<CapacitorAppInfo>;
}

export interface CapacitorRuntime {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: { App?: CapacitorAppPlugin };
}

let cachedMetadata: Promise<ClientMetadata> | undefined;

export function getClientMetadata(): Promise<ClientMetadata> {
  cachedMetadata ??= resolveClientMetadata(
    (window as unknown as { Capacitor?: CapacitorRuntime }).Capacitor,
  );
  return cachedMetadata;
}

export async function resolveClientMetadata(
  capacitor: CapacitorRuntime | undefined,
  webVersion = APP_VERSION,
): Promise<ClientMetadata> {
  const platform = capacitor?.getPlatform?.();
  const isNative =
    capacitor?.isNativePlatform?.() === true ||
    platform === "ios" ||
    platform === "android";
  const hostedWebVersion = normalizeReleaseValue(webVersion) ?? "unknown";

  if (!isNative || (platform !== "ios" && platform !== "android")) {
    return {
      surface: "web",
      appVersion: hostedWebVersion,
      webVersion: hostedWebVersion,
    };
  }

  let info: CapacitorAppInfo | undefined;
  try {
    info = await capacitor?.Plugins?.App?.getInfo?.();
  } catch {
    // Old native shells may not have @capacitor/app yet.
  }

  const appVersion = normalizeReleaseValue(info?.version) ?? "unknown";
  const buildNumber = normalizeReleaseValue(info?.build);
  return {
    surface: platform === "ios" ? "ios_app" : "android_app",
    appVersion,
    webVersion: hostedWebVersion,
    ...(buildNumber ? { buildNumber } : {}),
  };
}

export function normalizeReleaseValue(value: unknown): string | undefined {
  return typeof value === "string" &&
    value !== "all" &&
    value.length > 0 &&
    value.length <= MAX_CLIENT_RELEASE_VALUE_LENGTH &&
    /^[A-Za-z0-9][A-Za-z0-9._+-]*$/.test(value)
    ? value
    : undefined;
}
